import axios from '../../../common/api/request';
import { AxiosError } from 'axios';

export interface MarkdownImage {
  url: string;
  imagename: string;
  included: boolean;
}

export interface MarkdownVideo {
  url: string;
  videoname: string;
  included: boolean;
}

export interface MarkdownAttachment {
  url: string;
  filename: string;
  included: boolean;
}

export interface MarkdownContent {
  id: string;
  title: string;
  datatestid: string;
  pageurl: string;
  moduleid: string;
  type: string;
  content: string;
  enabled: boolean;
  imageList: MarkdownImage[];
  videoList: MarkdownVideo[];
  attachmentList: MarkdownAttachment[];
}

export interface UploadMarkdownRequest {
  id: string;
  title: string;
  datatestid: string;
  pageurl: string;
  moduleid: string;
  type: string;
  content: string;
  enabled: boolean;
  imageList: MarkdownImage[];
  videoList: MarkdownVideo[];
  attachmentList: MarkdownAttachment[];
}

export class MarkdownService {
  /**
   * Get fresh SAS token for images and videos
   */
  static async getSasToken(): Promise<string> {
    try {
      const response = await axios.get<{ token: string }>('/statics/token');
      
      // Response format: { "token": "sv=2023-08-03&se=2025-10-03T07%3A49%3A13Z&sr=c&sp=r&sig=..." }
      return response.data.token || '';
    } catch (error) {
      console.error('Error fetching SAS token:', error);
      throw new Error(`Failed to fetch SAS token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if content contains images, videos, or attachment links pointing to blob storage
   */
  static hasMediaContent(content: string): boolean {
    // Check for markdown images: ![alt](url)
    const hasMarkdownImages = /!\[.*?\]\(.*?\)/g.test(content);
    
    // Check for HTML images: <img src="url">
    const hasHtmlImages = /<img[^>]+src=["']([^"']+)["']/gi.test(content);
    
    // Check for HTML videos: <video> or <source>
    const hasHtmlVideos = /<video[^>]*>|<source[^>]+src=["']([^"']+)["']/gi.test(content);

    // Check for plain markdown links pointing to blob storage (attachments): [text](url)
    const hasAttachmentLinks = /(?<!!)(\[[^\]]*\]\(((?:[^)]*\/statics\/|[^)]*blob\.core\.windows\.net)[^)]*)\))/g.test(content);

    return hasMarkdownImages || hasHtmlImages || hasHtmlVideos || hasAttachmentLinks;
  }

  /**
   * Refresh SAS tokens in URLs
   */
  static refreshSasTokenInContent(content: string, newToken: string): string {
    if (!content || !newToken) return content;

    let updatedContent = content;

    // Replace SAS token in markdown image URLs: ![alt](url?oldToken)
    updatedContent = updatedContent.replace(
      /!\[([^\]]*)\]\(([^)]+?)(\?[^)]*?)?\)/g,
      (match, alt, url, queryString) => {
        // If URL contains /statics/ or is a blob storage URL, refresh token
        if (url.includes('/statics/') || url.includes('blob.core.windows.net')) {
          // Remove old query string and add new token
          const cleanUrl = url.split('?')[0];
          return `![${alt}](${cleanUrl}?${newToken})`;
        }
        return match;
      }
    );

    // Replace SAS token in HTML img src: <img src="url?oldToken">
    updatedContent = updatedContent.replace(
      /<img([^>]*?)src=["']([^"']+?)["']([^>]*?)>/gi,
      (match, before, url, after) => {
        if (url.includes('/statics/') || url.includes('blob.core.windows.net')) {
          const cleanUrl = url.split('?')[0];
          return `<img${before}src="${cleanUrl}?${newToken}"${after}>`;
        }
        return match;
      }
    );

    // Replace SAS token in HTML video/source src: <source src="url?oldToken">
    updatedContent = updatedContent.replace(
      /<source([^>]*?)src=["']([^"']+?)["']([^>]*?)>/gi,
      (match, before, url, after) => {
        if (url.includes('/statics/') || url.includes('blob.core.windows.net')) {
          const cleanUrl = url.split('?')[0];
          return `<source${before}src="${cleanUrl}?${newToken}"${after}>`;
        }
        return match;
      }
    );

    // Replace SAS token in plain markdown links (attachments): [text](url?oldToken)
    // Uses negative lookbehind to avoid matching image links (![alt](url))
    updatedContent = updatedContent.replace(
      /(?<!!)(\[([^\]]*)\]\(([^)]+?)(\?[^)]*?)?\))/g,
      (match, _full, _text, url, _queryString) => {
        if (url.includes('/statics/') || url.includes('blob.core.windows.net')) {
          const cleanUrl = url.split('?')[0];
          // Reconstruct: [text](cleanUrl?newToken)
          return match.replace(`(${url}${_queryString || ''})`, `(${cleanUrl}?${newToken})`);
        }
        return match;
      }
    );

    return updatedContent;
  }

  /**
   * Process markdown content to refresh SAS tokens if needed
   */
  static async processContentWithFreshToken(content: MarkdownContent): Promise<MarkdownContent> {
    // Check if content has media
    const hasMedia = this.hasMediaContent(content.content);
    
    if (!hasMedia) {
      return content;
    }

    try {
      // Get fresh SAS token
      const freshToken = await this.getSasToken();
      
      // Refresh tokens in content
      const updatedContent = this.refreshSasTokenInContent(content.content, freshToken);
      
      // Refresh tokens in image list URLs
      const updatedImageList = content.imageList?.map(img => ({
        ...img,
        url: img.url.includes('/statics/') || img.url.includes('blob.core.windows.net')
          ? `${img.url.split('?')[0]}?${freshToken}`
          : img.url
      })) || [];

      // Refresh tokens in video list URLs
      const updatedVideoList = content.videoList?.map(video => ({
        ...video,
        url: video.url.includes('/statics/') || video.url.includes('blob.core.windows.net')
          ? `${video.url.split('?')[0]}?${freshToken}`
          : video.url
      })) || [];

      // Refresh tokens in attachment list URLs
      const updatedAttachmentList = content.attachmentList?.map(attachment => ({
        ...attachment,
        url: attachment.url.includes('/statics/') || attachment.url.includes('blob.core.windows.net')
          ? `${attachment.url.split('?')[0]}?${freshToken}`
          : attachment.url
      })) || [];

      return {
        ...content,
        content: updatedContent,
        imageList: updatedImageList,
        videoList: updatedVideoList,
        attachmentList: updatedAttachmentList,
      };
    } catch (error) {
      console.error('❌ Token refresh error:', error instanceof Error ? error.message : 'Unknown error');
      return content;
    }
  }

  /**
   * Get all markdown content
   */
  static async getAllMarkdowns(): Promise<MarkdownContent[]> {
    try {
      const response = await axios.get<MarkdownContent[]>('/statics/help');
      
      // Process each content to refresh SAS tokens if needed
      const processedContent = await Promise.all(
        response.data.map(content => this.processContentWithFreshToken(content))
      );
      
      return processedContent;
    } catch (error) {
      console.error('Error fetching all markdowns:', error);
      throw new Error(`Failed to fetch help content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get markdown content by page URL
   */
  static async getMarkdownByPageUrl(pageUrl: string): Promise<MarkdownContent[]> {
    try {
      // Don't encode the entire URL, just ensure it starts with /
      const cleanPageUrl = pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`;
      const fullUrl = `/statics/markdowns/by-pageurl?pageurl=${encodeURIComponent(cleanPageUrl)}`;

      
      const response = await axios.get<MarkdownContent[]>(fullUrl);
      
      // Process each content to refresh SAS tokens if needed
      const processedContent = await Promise.all(
        response.data.map(content => this.processContentWithFreshToken(content))
      );
      
      return processedContent;
    } catch (error) {
      throw new Error(`Failed to fetch help content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get specific markdown by ID
   */
  static async getMarkdownById(markdownId: string): Promise<MarkdownContent> {
    try {
      const fullUrl = `/statics/markdowns/${markdownId}`;
      
      const response = await axios.get<MarkdownContent>(fullUrl);
      
      // Process content to refresh SAS tokens if needed
      const processedContent = await this.processContentWithFreshToken(response.data);
      
      return processedContent;
    } catch (error) {
      throw new Error(`Failed to fetch help content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Upload markdown content
   */
  static async uploadMarkdown(markdownData: UploadMarkdownRequest): Promise<MarkdownContent> {
    try {
      const response = await axios.post<MarkdownContent>('/statics/uploadMarkdown', markdownData);
      return response.data;
    } catch (error) {
      // Extract error message from API response
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as { message?: string; status?: string } | undefined;
        const errorMessage = errorData?.message || error.message || 'Failed to upload help content';
        throw new Error(errorMessage);
      }
      
      // Fallback for non-axios errors
      throw new Error(`Failed to upload help content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }


  /**
   * Upload file for markdown content
   */
  static async uploadFile(
    file: File, 
    contentType: 'image' | 'video' | 'attachment',
    onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    // Generate a UUID for the upload
    const uuid = crypto.randomUUID();
    const uploadUrl = `/statics/${uuid}/${contentType}/upload`;

    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
            });
          }
        }
      });

      // The response should contain the URL where the file is accessible
      const result = response.data;
      const fileUrl = result.url || result;
      
      return fileUrl;
    } catch (error) {
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete markdown content
   */
  static async deleteMarkdown(markdownId: string): Promise<void> {
    try {
      const response = await axios.delete(`/statics/deleteMarkdown/${markdownId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to delete markdown: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
